import { useTranslation } from 'react-i18next';

import {
  blockKey,
  checklistItemKey,
  itemLabel,
  type HandoverChecklistBlock,
  type HandoverChecklistItem,
} from '@entities/handover';
import { Checkbox } from '@shared/ui';

import styles from './Handover.module.css';

interface HandoverChecklistProps {
  block: HandoverChecklistBlock | null;
  /** Заказ завершён: чек-лист остаётся на чтение (§39.3). */
  readOnly: boolean;
  pending: boolean;
  onToggle: (key: string, done: boolean) => void;
}

/**
 * Один блок чек-листа сопровождения (FE-260, MVP_PRD §39.1).
 *
 * Четыре правила блока:
 *
 * * **пункт всегда называет основание.** «Проверить лично» без указания, что
 *   именно и почему, — тревожная надпись, а не чек-лист. Основание приходит
 *   кодом с параметрами и переводится через i18n (CLAUDE.md §6);
 * * **ни одного суждения о состоянии автомобиля.** Пункт говорит «сверьте» и
 *   «пересчитайте», а не «убедитесь, что исправно»: диагноза CarViQ.DE не ставит
 *   и серьёзность находки не оценивает (§2);
 * * **счётчик — это число, а не оценка.** Пройденный чек-лист ничего не
 *   утверждает об автомобиле: он говорит только о том, что пользователь
 *   посмотрел сам;
 * * **отметка обратима.** Нажатие снимается тем же нажатием, поэтому здесь нет
 *   ни подтверждения в два шага, ни ключа идемпотентности: объявлять
 *   необратимым то, что таковым не является, — это ложная тревога.
 *
 * Пустой блок (у «до покупки» так бывает: автохаус ответил на всё, расхождений
 * нет) показывается **честной строкой**, а не исчезает: отсутствие пунктов —
 * это результат работы, а не сбой загрузки.
 */
export function HandoverChecklist({ block, readOnly, pending, onToggle }: HandoverChecklistProps) {
  const { t } = useTranslation();

  if (block === null) return null;

  return (
    <section className={styles.section} aria-labelledby={`handover-block-${block.block}`}>
      <div className={styles.blockHead}>
        <h2 id={`handover-block-${block.block}`} className={styles.title}>
          {t(blockKey(block.block))}
        </h2>
        <span className={styles.progress}>
          {t('handover.checklist.progress', { done: block.done, total: block.total })}
        </span>
      </div>
      <p className={styles.subtitle}>{t(`handover.blockHint.${block.block}`)}</p>

      {block.items.length === 0 ? (
        <p className={styles.note}>{t(`handover.checklist.empty.${block.block}`)}</p>
      ) : (
        <ul className={styles.items}>
          {block.items.map((item) => (
            <li key={item.key} className={styles.item}>
              <Checkbox
                checked={item.done_at !== null}
                disabled={readOnly || pending}
                onChange={(event) => {
                  onToggle(item.key, event.currentTarget.checked);
                }}
              >
                {t(checklistItemKey(item.block, item.code), itemParams(item))}
              </Checkbox>
              {item.quote === null ? null : (
                <blockquote className={styles.itemQuote}>«{item.quote}»</blockquote>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Пройденный чек-лист ничего не утверждает об автомобиле (§2). */}
      <p className={styles.note}>{t('handover.checklist.note')}</p>
    </section>
  );
}

/**
 * Параметры подстановки для строки пункта.
 *
 * Коды-основания (`attention_code`, `question_code`, `field_code`) переводит
 * **сама строка словаря** через вложение i18next (`$t(...)`): так словарь
 * остаётся единственным местом, где машинный код превращается в текст, и
 * второго правила подстановки в коде компонента не заводится.
 *
 * Позиция, обещанная автохаусом, приходит **его словами** (`label`) — они
 * подставляются как параметр, а не переписываются: своего названия у обещания
 * у CarViQ.DE нет (§39.1).
 */
function itemParams(item: HandoverChecklistItem): Record<string, unknown> {
  const label = itemLabel(item);
  const params: Record<string, unknown> = { ...item.params };
  if (label !== null) params['label'] = label;
  return params;
}
